import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Calendar, MapPin, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';

/**
 * A3. Global filters context — one date range + one property scope
 * that any widget on the Home dashboard (or elsewhere) can subscribe
 * to instead of duplicating per-widget scope controls.
 *
 * Usage:
 *   <GlobalFiltersProvider properties={props}>
 *     <GlobalFilterBar />
 *     <SomeWidget />   // reads useGlobalFilters()
 *   </GlobalFiltersProvider>
 *
 * Values are persisted in localStorage so a reload keeps the user in
 * the same scope.
 */

const STORAGE_KEY = 'lodgiflow_global_filters_v1';
const RANGES = [
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
  { id: 'ytd', label: 'Year to date', days: 365 },
  { id: 'all', label: 'All time', days: null },
];

const GlobalFiltersContext = createContext(null);

export function GlobalFiltersProvider({ children, properties = [] }) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { rangeId: '30d', propertyId: 'all', ...JSON.parse(raw) };
    } catch {}
    return { rangeId: '30d', propertyId: 'all' };
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
  }, [state]);

  const setRange = useCallback((id) => setState(s => ({ ...s, rangeId: id })), []);
  const setProperty = useCallback((id) => setState(s => ({ ...s, propertyId: id })), []);

  const range = useMemo(() => RANGES.find(r => r.id === state.rangeId) || RANGES[1], [state.rangeId]);
  const value = { ...state, setRange, setProperty, range, properties };
  return <GlobalFiltersContext.Provider value={value}>{children}</GlobalFiltersContext.Provider>;
}

export function useGlobalFilters() {
  const ctx = useContext(GlobalFiltersContext);
  if (!ctx) return null;
  return ctx;
}

/**
 * Compact date + property scope bar. Drop above any widget grid.
 * Widgets that want to react should call useGlobalFilters().
 */
export default function GlobalFilterBar({ className = '', showProperty = true }) {
  const ctx = useGlobalFilters();
  if (!ctx) return null;
  const { rangeId, setRange, propertyId, setProperty, properties } = ctx;

  const activeRange = RANGES.find(r => r.id === rangeId) || RANGES[1];
  const activeProp = properties.find(p => p.id === propertyId);
  const propertyLabel = propertyId === 'all' ? 'All properties' : (activeProp?.name || 'Select property');

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-xl border bg-card/70 backdrop-blur px-2 py-1.5 ${className}`}
      data-testid="global-filter-bar"
    >
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-[11px] font-medium"
            data-testid="global-filter-range-trigger"
          >
            <Calendar className="h-3 w-3" />
            {activeRange.label}
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-44 p-1" data-testid="global-filter-range-popover">
          {RANGES.map(r => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              className={`w-full text-left px-2 py-1.5 rounded-md text-xs ${rangeId === r.id ? 'bg-violet-100 text-violet-700 font-semibold' : 'hover:bg-muted'}`}
              data-testid={`global-filter-range-${r.id}`}
            >
              {r.label}
            </button>
          ))}
        </PopoverContent>
      </Popover>

      {showProperty && (
        <>
          <div className="h-4 w-px bg-border" />
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-[11px] font-medium max-w-[220px]"
                data-testid="global-filter-property-trigger"
              >
                <MapPin className="h-3 w-3" />
                <span className="truncate">{propertyLabel}</span>
                <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-1 max-h-72 overflow-y-auto" data-testid="global-filter-property-popover">
              <button
                onClick={() => setProperty('all')}
                className={`w-full text-left px-2 py-1.5 rounded-md text-xs ${propertyId === 'all' ? 'bg-violet-100 text-violet-700 font-semibold' : 'hover:bg-muted'}`}
                data-testid="global-filter-property-all"
              >
                All properties
              </button>
              {properties.map(p => (
                <button
                  key={p.id}
                  onClick={() => setProperty(p.id)}
                  className={`w-full text-left px-2 py-1.5 rounded-md text-xs truncate ${propertyId === p.id ? 'bg-violet-100 text-violet-700 font-semibold' : 'hover:bg-muted'}`}
                  data-testid={`global-filter-property-${p.id}`}
                >
                  {p.name}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </>
      )}
    </div>
  );
}
